import { WithAppStore } from "@/store/types";
import {
  action,
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  override,
  runInAction,
} from "mobx";
import {
  IndexedNoteSyncUpdateValue,
  INoteObservable,
  NoteContentVersion,
  NoteMediaKind,
  NoteModelData,
} from "@/store/note/types";
import { Uuid } from "@/domains/global/identifiers";
import { SyncModelKind } from "@/store/sync/types";
import {
  GrantableSyncScopeRoleKind,
  SyncModelPermissionEntryWithStatus,
} from "@/domains/sync-scopes/types";
import { resolveFavoriteItemSyncModelUuid } from "@/modules/uuid/sync-models/resolveFavoriteItemSyncModelUuid";
import { UNTITLED_NOTE_TITLE } from "@/domains/untitled/untitled";
import { filter, isEqual, uniq } from "lodash-es";
import { NoteCollectionListObservable } from "@/store/collection-items/NoteCollectionListObservable";
import { resolveSpaceAccountNoteSyncModelUuid } from "@/modules/uuid/sync-models/resolveSpaceAccountNoteSyncModelUuid";
import { SpaceAccountNoteObservable } from "@/store/recent-items/SpaceAccountNoteObservable";
import { BaseSyncModel } from "@/store/sync/BaseSyncModel";
import { FavoriteItemObservable } from "@/store/favorite-items/FavoriteItemObservable";
import { IContactModel } from "@/store/contacts/types";
import { getQueuedDocumentUpdates } from "@/store/sync/operations/helpers/notes/getQueuedDocumentUpdates";
import { UpdateNoteContentUsingDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingDiffOperation";
import { DeleteNoteOperation } from "@/store/sync/operations/notes/DeleteNoteOperation";
import { TrashNoteOperation } from "@/store/sync/operations/notes/TrashNoteOperation";
import { RestoreNoteOperation } from "@/store/sync/operations/notes/RestoreNoteOperation";
import { AddNoteToFavoritesOperation } from "@/store/sync/operations/favorites/AddNoteToFavoritesOperation";
import { RemoveNoteFromFavoritesOperation } from "@/store/sync/operations/favorites/RemoveNoteFromFavoritesOperation";
import { MarkNoteViewedOperation } from "@/store/sync/operations/recents/MarkNoteViewedOperation";
import { GrantNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/GrantNoteAclViaSpaceAccountOperation";
import { GrantNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/GrantNoteAclViaEmailAddressOperation";
import { UpdateNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaSpaceAccountOperation";
import { UpdateNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaEmailAddressOperation";
import { UpdateNoteAclViaCollectionOperation } from "@/store/sync/operations/notes/UpdateNoteAclViaCollectionOperation";
import { RevokeNoteAclViaSpaceAccountOperation } from "@/store/sync/operations/notes/RevokeNoteAclViaSpaceAccountOperation";
import { RevokeNoteAclViaEmailAddressOperation } from "@/store/sync/operations/notes/RevokeNoteAclViaEmailAddressOperation";
import { logger } from "@/modules/logger";
import { getPermissionsForNoteSyncModel } from "@/store/sync/operations/helpers/permissions/getPermissionsForModel";
import { resolveNoteContentDocumentSyncModelUuid } from "@/modules/uuid/sync-models/resolveNoteContentDocumentSyncModelUuid";
import { NoteContentDocumentObservable } from "@/store/note/NoteContentDocumentObservable";
import {
  AsyncData,
  asyncDataFailed,
  asyncDataLoaded,
  asyncDataLoading,
} from "@/domains/async/AsyncData";
import { appRoutes } from "@/app/router";
import { notesModule } from "@/modules/notes";
import { uuidModule } from "@/modules/uuid";
import { NoteTopicListObservable } from "@/store/topics/NoteTopicListObservable";
import {
  ExtractedNoteMention,
  MemCommonEditorContext,
  MemCommonEditorFileInfo,
  MemCommonEditorImageInfo,
  yjsModule,
} from "@mem-labs/common-editor";
import { UploadImageExternalOperation } from "@/store/sync/operations/external/UploadImageExternalOperation";
import { UploadFileExternalOperation } from "@/store/sync/operations/external/UploadFileExternalOperation";
import { ExternalOperationKind } from "@/store/sync/operations/types";
import { EventContext } from "@/domains/metrics/context";
import { JsonValue } from "type-fest";
import { liveQuery } from "dexie";
import { UpdateNoteContentUsingTemplateAndDiffOperation } from "@/store/sync/operations/notes/UpdateNoteContentUsingTemplateAndDiffOperation";
import { NoteSourceListObservable } from "@/store/sources/NoteSourceListObservable";
import { UpdateNoteContentOverwritingFullStateDestructivelyOperation } from "@/store/sync/operations/notes/UpdateNoteContentOverwritingFullStateDestructivelyOperation";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { AcceptNoteSuggestionOperation } from "@/store/sync/operations/notes/AcceptNoteSuggestionOperation";
import { RejectNoteSuggestionOperation } from "@/store/sync/operations/notes/RejectNoteSuggestionOperation";
import { SpaceAccountNoteModelData } from "@/store/recent-items/types";
import { cleanupNote } from "@/actions/cleanupNote";
import { refineNote } from "@/actions/refineNote";
import { refineNoteWithVoice } from "@/actions/refineNoteWithVoice";
import { truncateSuggestionDataForTracking } from "@/store/note/helpers/suggestionTracking";
import { AutoCleanupResult } from "@/modules/notes/auto-cleanup-policy";

export class NoteObservable extends BaseSyncModel<NoteModelData> implements INoteObservable {
  public collectionList: NoteCollectionListObservable;
  public sourceList: NoteSourceListObservable;
  public noteContentDocument: AsyncData<NoteContentDocumentObservable> = asyncDataLoading({});
  public spaceAccountTopics: NoteTopicListObservable;
  public modelKind = SyncModelKind.Note;

  // INDEXES
  public createdAt: string;
  public modifiedAt: string;
  public trashedAt: string | null;
  public lastViewedAt: string;
  public isTrashed: boolean;

  private primaryLabel: string = "";
  private secondaryLabel: string = "";
  private locked: boolean = false;

  isProcessingSuggestion: boolean = false;

  constructor({
    id,
    data,
    store,
  }: {
    id: Uuid;
    data: IndexedNoteSyncUpdateValue;
  } & WithAppStore) {
    super({ id, data, store });
    this.collectionList = new NoteCollectionListObservable({ noteId: id, store });
    this.sourceList = new NoteSourceListObservable({ noteId: id, store });
    this.spaceAccountTopics = new NoteTopicListObservable({ noteId: id, store });

    this.createdAt = data.created_at;
    this.modifiedAt = data.modified_at;
    this.trashedAt = data.trashed_at || null;
    this.lastViewedAt = data.last_viewed_at;
    this.isTrashed = data.is_trashed === 1;
    this.primaryLabel = data.primary_label || "";
    this.secondaryLabel = data.model_data.secondary_label || "";

    makeObservable<
      NoteObservable,
      | "primaryLabel"
      | "locked"
      | "secondaryLabel"
      | "updateLabels"
      | "notifyHeadsUpOfAutoGenerationReadiness"
    >(this, {
      modelKind: observable,
      // INDEXES
      createdAt: observable,
      modifiedAt: observable,
      trashedAt: observable,
      lastViewedAt: observable,
      isTrashed: observable,

      updateFromLocal: override,
      updateFromData: action,
      updateFromRemote: override,
      subscribeToLocal: override,

      // OBSERVABLES
      noteContentDocument: observable,
      collectionList: observable,
      sourceList: observable,
      spaceAccountTopics: observable,

      // OVERRIDES
      isAvailable: override,
      isShared: override,
      permissions: override,

      // PROPERTIES
      memoryQueue: computed,
      receivedAt: computed,
      lastMentionedAt: computed,
      locallyCreatedAt: computed,
      title: computed,
      secondaryTitle: computed,
      path: computed,
      primaryOwner: computed,
      authors: computed,
      metadata: computed,
      shared_with_me_at: computed,
      primaryLabel: observable,
      secondaryLabel: observable,
      locked: observable,
      importBatchId: computed,
      hasSources: computed,
      primaryLabelText: computed,
      hasPrimaryLabel: computed,
      notifyHeadsUpOfAutoGenerationReadiness: false,

      // NOTE CONTENT
      noteContentDocumentId: computed,
      setNoteContentDocument: action,
      isNoteContentDocumentLoaded: computed,
      fetchNoteContentDocument: action,
      remoteContent: computed,
      isNoteContentInOriginalState: computed,
      queuedDocumentUpdates: computed,

      // FAVORITES
      favoriteItemId: computed,
      favoriteItem: computed,
      isFavorited: computed,
      isLocked: computed,

      // SPACE ACCOUNT
      spaceAccountNoteId: computed,
      spaceAccountNote: computed,

      // ACTIONS
      updateContentUsingDiff: false,
      acceptNoteContentChanges: false,
      saveAsNewNote: action,
      delete: action,
      deleteEmptyNote: action,
      moveToTrash: action,
      restoreFromTrash: action,
      grantAccessViaSpaceAccount: action,
      grantAccessViaEmailAddress: action,
      updateAccessViaSpaceAccount: action,
      updateAccessViaEmailAddress: action,
      updateAccessViaCollection: action,
      revokeAccessViaSpaceAccount: action,
      revokeAccessViaEmailAddress: action,
      toggleFavorite: action,
      addToRecents: action,
      uploadImageAssociatedWithNote: action,
      uploadFileAssociatedWithNote: action,
      generateCommonEditorContext: action,
      lock: action,
      unlock: action,
      updateLabels: action,

      // Suggestions
      isProcessingSuggestion: observable,
      setIsProcessingSuggestion: action,
      isSuggestionActive: computed,
      suggestionData: computed,
      rejectSuggestion: action,
      acceptSuggestion: action,
      refineNoteSuggestion: action,
      cleanup: action,

      // Audio
      isAudioRecordingActive: computed,
      hasFinishedAudioRecording: computed,
    });

    onBecomeObserved(this, "isNoteContentDocumentLoaded", () => {
      this.fetchNoteContentDocument();
    });
  }

  async subscribeToLocal() {
    this.localSubscription?.unsubscribe();
    this.localSubscription = liveQuery(() => this.store.notes.localTable.get(this.id)).subscribe({
      next: data =>
        runInAction(() => {
          if (data) this.updateFromData(data);
        }),
    });
  }

  async updateFromLocal() {
    const data = await this.store.notes.localTable.get(this.id);
    if (data) runInAction(() => this.updateFromData(data));
  }

  updateFromData(data: IndexedNoteSyncUpdateValue) {
    this.data = data;
    this.createdAt = data.created_at;
    this.modifiedAt = data.modified_at;
    this.trashedAt = data.trashed_at || null;
    this.isTrashed = data.is_trashed === 1;
    this.lastViewedAt = data.last_viewed_at;
    if (!this.locked) {
      this.primaryLabel = data.primary_label || "";
      this.secondaryLabel = data.model_data.secondary_label || "";
    }

    if (this.existsRemotely) {
      this.store.headsUp.markNoteSyncedForAutoGeneration(this.id);
    }

    this.notifyHeadsUpOfAutoGenerationReadiness({
      primaryLabel: this.primaryLabel,
      isEmpty: Boolean(data.model_data.is_empty),
    });
  }

  async updateFromRemote() {
    await super.updateFromRemote();

    if (this.existsRemotely) {
      this.store.headsUp.markNoteSyncedForAutoGeneration(this.id);
    }
  }

  public lock() {
    logger.debug({
      message: `[NoteObservable][lock][debug] ${this.id} Locked.`,
    });

    this.locked = true;
  }

  public unlock() {
    logger.debug({
      message: `[NoteObservable][unlock][debug] ${this.id} Unlocked.`,
    });

    this.locked = false;
  }

  public get isLocked() {
    return this.locked;
  }

  // PROPERTIES
  get memoryQueue() {
    return this.store.notes.getNoteQueue({ noteId: this.id });
  }

  // TODO: Implement proper received_at when ready
  get receivedAt(): string | null | undefined {
    return this.modelData.created_at;
  }

  get lastMentionedAt(): string | undefined {
    return undefined;
  }

  get locallyCreatedAt(): string {
    return this.modelData.locally_created_at;
  }

  get isShared(): boolean {
    const output = super.isShared;
    for (const collection of this.collections) if (collection.isShared) return true;
    return output;
  }

  get shared_with_me_at(): string {
    return this.spaceAccountNote?.modelData.shared_with_me_at || "";
  }

  get isAvailable(): boolean {
    return !this.isDeleted && this.canAccess && !this.isTrashed;
  }

  get permissions(): SyncModelPermissionEntryWithStatus[] {
    return getPermissionsForNoteSyncModel({
      id: this.id,
      remoteData: this.remoteData,
      store: this.store,
      actionQueue: this.store.sync.actionQueue,
    });
  }

  get title(): string {
    // We need to access the modelData first to make sure that we subscribeToLocal when this is accessed
    const modelPrimaryLabel = this.modelData.primary_label;
    return this.primaryLabel || modelPrimaryLabel || UNTITLED_NOTE_TITLE;
  }

  get secondaryTitle(): string {
    // We need to access the modelData first to make sure that we subscribeToLocal when this is accessed
    const modelSecondaryLabel = this.modelData.secondary_label;
    return this.secondaryLabel || modelSecondaryLabel || "";
  }

  get hasPrimaryLabel(): boolean {
    if (this.primaryLabel.trim()) return true;
    return Boolean(this.modelData.primary_label?.trim());
  }

  get primaryLabelText(): string {
    // We need to access the modelData first to make sure that we subscribeToLocal when this is accessed
    const modelPrimaryLabel = this.modelData.primary_label;
    return this.primaryLabel || modelPrimaryLabel || "";
  }

  get path(): string {
    return appRoutes.notesView({ params: { noteId: this.id } }).path;
  }

  get primaryOwner(): IContactModel | undefined {
    const spaceAccountId = this.data.model_data.owned_by_space_account_id;
    const spaceAccount =
      spaceAccountId === this.store.spaceAccounts.myPersonalSpaceAccountId
        ? this.store.spaceAccounts.myPersonalSpaceAccount
        : this.store.contacts.getBySpaceAccountId(spaceAccountId);
    return spaceAccount;
  }

  get authors(): IContactModel[] {
    const spaceAccountIds = uniq([
      this.data.model_data.owned_by_space_account_id,
      ...this.data.model_data.modified_by_space_account_ids,
      ...this.remoteData.model_data.modified_by_space_account_ids,
    ]);
    const getContactObservableByContactSpaceAccountId = (spaceAccountId: string) => {
      if (spaceAccountId === this.store.spaceAccounts.myPersonalSpaceAccountId) {
        return this.store.spaceAccounts.myPersonalSpaceAccount;
      }
      return this.store.contacts.getBySpaceAccountId(spaceAccountId);
    };
    const contacts = spaceAccountIds.map(getContactObservableByContactSpaceAccountId);
    return filter(contacts) as IContactModel[];
  }

  get metadata(): string {
    // TODO: implement
    return "";
  }

  // NOTE CONTENT
  get noteContentDocumentId(): Uuid {
    return resolveNoteContentDocumentSyncModelUuid({ noteId: this.id });
  }

  setNoteContentDocument(noteContentDocument: NoteContentDocumentObservable) {
    if (
      this.noteContentDocument.data &&
      isEqual(this.noteContentDocument.data.id, noteContentDocument.id) &&
      isEqual(this.noteContentDocument.data.data, noteContentDocument.data) &&
      isEqual(this.noteContentDocument.data.remoteData, noteContentDocument.remoteData)
    ) {
      return;
    }

    this.noteContentDocument = asyncDataLoaded(this.noteContentDocument, noteContentDocument);
  }

  /**
   * Note content starts in the loading state.
   */
  get isNoteContentDocumentLoaded(): boolean {
    if (this.noteContentDocument.isLoading) return false;

    if (!this.noteContentDocument.data) {
      logger.info({
        message: `[NoteObservable][isNoteContentDocumentLoaded] Loaded, but no data.`,
        info: {
          noteId: this.id,
          noteContentDocumentId: this.noteContentDocumentId,
        },
      });

      return false;
    }

    return true;
  }

  get importBatchId(): string | null {
    return this.modelData.import_batch_id || null;
  }

  get hasSources(): boolean {
    return this.sourceList?.allSupportedSources.length > 0;
  }

  async fetchNoteContentDocument() {
    if (this.isNoteContentDocumentLoaded) return;

    const logInfo = {
      noteId: this.id,
      noteContentDocumentId: this.noteContentDocumentId,
    };

    logger.info({
      message: `[NoteObservable][fetchNoteContentDocument] Started.`,
      info: logInfo,
    });

    const noteContentDocument = await this.store.noteContentDocuments.getAsync(
      this.noteContentDocumentId
    );

    if (noteContentDocument) {
      this.setNoteContentDocument(noteContentDocument);

      logger.info({
        message: `[NoteObservable][fetchNoteContentDocument] Got from local.`,
        info: logInfo,
      });

      return;
    }

    runInAction(() => {
      this.noteContentDocument = asyncDataLoading(this.noteContentDocument);
    });

    const fetchedNoteContentDocument = await this.store.noteContentDocuments.fetch(
      this.noteContentDocumentId
    );

    if (fetchedNoteContentDocument) {
      this.setNoteContentDocument(fetchedNoteContentDocument);
    } else {
      runInAction(() => {
        this.noteContentDocument = asyncDataFailed(this.noteContentDocument);
      });
    }

    logger.info({
      message: `[NoteObservable][fetchNoteContentDocument] Fetched from remote.`,
      info: {
        ...logInfo,
        fetchResult: !!fetchedNoteContentDocument,
      },
    });
  }

  get remoteContent(): string | null {
    return this.noteContentDocument.data?.remoteContent || null;
  }

  get isNoteContentInOriginalState() {
    // Not in original state if there are things in the queue
    const queuedDocumentUpdates = this.queuedDocumentUpdates;
    if (queuedDocumentUpdates.length > 0) return false;

    // Versions:
    // 0: Created locally.
    // 1: Synced to server.
    // 2+: Content updates.
    return this.modelVersion <= 1;
  }

  get queuedDocumentUpdates() {
    return getQueuedDocumentUpdates({
      operationsByModelId: this.store.sync.actionQueue.operationsByModelId,
      id: this.id,
    });
  }

  // FAVORITES
  get favoriteItemId(): Uuid {
    return resolveFavoriteItemSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      itemId: this.id,
    });
  }

  get favoriteItem(): FavoriteItemObservable | undefined {
    return this.store.favoriteItems.get(this.favoriteItemId);
  }

  get isFavorited(): boolean {
    return this.store.favoriteItems.has(this.favoriteItemId);
  }

  // SPACE ACCOUNT NOTE
  get spaceAccountNoteId(): Uuid {
    return resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId: this.store.spaceAccounts.myPersonalSpaceAccountId,
      noteId: this.id,
    });
  }

  get spaceAccountNote(): SpaceAccountNoteObservable | undefined {
    return this.store.spaceAccountNotes.get(this.spaceAccountNoteId);
  }

  // ACTIONS
  public updateContentUsingDiff({
    encodedContentDiff,
    primaryLabel,
    secondaryLabel,
    mediaKinds,
    isEmpty,
    transactionMetadata,
    tiptapJsonContent,
    mentions,
    templateId,
  }: {
    encodedContentDiff: string | null;
    primaryLabel: string;
    secondaryLabel: string;
    mediaKinds?: NoteMediaKind[];
    transactionMetadata?: Record<string, JsonValue>;
    tiptapJsonContent?: string;
    mentions?: ExtractedNoteMention[];
    templateId?: string;
    isEmpty?: boolean;
  }) {
    const noteIsEmpty = Boolean(isEmpty);

    if (!encodedContentDiff) {
      this.updateLabels(primaryLabel, secondaryLabel);
    } else {
      // TODO: Handle null properly
      this.memoryQueue.push(
        templateId
          ? new UpdateNoteContentUsingTemplateAndDiffOperation({
              store: this.store,
              payload: {
                note_id: this.id,
                encoded_content_diff: encodedContentDiff || "",
                template_id: templateId,
              },
              primaryLabel,
              secondaryLabel,
              mediaKinds,
              transactionMetadata,
              tiptapJsonContent,
              mentions,
            })
          : new UpdateNoteContentUsingDiffOperation({
              store: this.store,
              payload: {
                id: this.id,
                encoded_content_diff: encodedContentDiff || "",
                is_silent: transactionMetadata?.["addToHistory"] === false,
              },
              primaryLabel,
              secondaryLabel,
              mediaKinds,
              transactionMetadata,
              tiptapJsonContent,
              mentions,
              isEmpty: noteIsEmpty,
            }),
        encodedContentDiff ? { dropUniqueId: METADATA_ID } : { uniqueId: METADATA_ID }
      );
    }

    this.notifyHeadsUpOfAutoGenerationReadiness({ primaryLabel, isEmpty: noteIsEmpty });

    this.store.headsUp.handleEditorUpdate({
      noteId: this.id,
      primaryLabel,
      isEmpty: noteIsEmpty,
      transactionMetadata,
    });
  }

  public acceptNoteContentChanges(
    version: Omit<NoteContentVersion, "model_version">,
    baseVersion?: Omit<NoteContentVersion, "model_version">,
    args?: { overwrite?: boolean; patchId?: string }
  ) {
    const encodedContent = (() => {
      const encodedContent = version.model_data.encoded_content;
      if (!encodedContent) return;

      const baseEncodedContent = baseVersion?.model_data.encoded_content;
      if (baseEncodedContent) {
        return yjsModule.mergeDiffsWithRemoteContent(baseEncodedContent, [encodedContent]);
      }
      return encodedContent;
    })();

    if (!encodedContent) return;
    const { primaryLabel, secondaryLabel, mediaKinds, mentions } =
      notesModule.convertEncodedContentToNoteContent(encodedContent);

    const operation = args?.overwrite
      ? new UpdateNoteContentOverwritingFullStateDestructivelyOperation({
          store: this.store,
          payload: {
            id: this.id,
            encoded_content: encodedContent,
          },
          primaryLabel: primaryLabel || "",
          secondaryLabel: secondaryLabel || "",
          mediaKinds: mediaKinds || [],
          mentions: mentions || [],
        })
      : // TODO: send patchId to server, in the end this should look like UpdateNoteContentUsingTemplateAndDiffOperation
        new UpdateNoteContentUsingDiffOperation({
          store: this.store,
          payload: {
            id: this.id,
            encoded_content_diff: encodedContent,
            is_silent: false, // This needs to false to trigger an sync update for NoteSyncModel
          },
          primaryLabel: primaryLabel || "",
          secondaryLabel: secondaryLabel || "",
          mediaKinds: mediaKinds || [],
          transactionMetadata: { addToHistory: false },
          tiptapJsonContent: "",
          mentions: [],
          isEmpty: false,
        });

    this.memoryQueue.push(operation, { dropUniqueId: METADATA_ID });

    this.store.interface.notifyReloadRequired(this.id);
  }

  public async saveAsNewNote({ eventContext }: { eventContext: EventContext }) {
    const remoteContent = this.remoteContent;
    const diffs = this.queuedDocumentUpdates.map(update => update.encodedContentDiff);
    const newEncodedContent = yjsModule.mergeDiffsWithRemoteContent(remoteContent, diffs);
    const { primaryLabel, secondaryLabel, mediaKinds } =
      notesModule.convertEncodedContentToNoteContent(newEncodedContent);
    const newNoteId = uuidModule.generate();
    await this.store.notes.createNote({ noteId: newNoteId, eventContext });
    const queue = this.store.notes.getNoteQueue({ noteId: newNoteId });
    queue.push(
      new UpdateNoteContentUsingDiffOperation({
        store: this.store,
        payload: {
          id: newNoteId,
          encoded_content_diff: newEncodedContent,
        },
        mediaKinds: mediaKinds || [],
        primaryLabel: primaryLabel || "",
        secondaryLabel: secondaryLabel || "",
      })
    );
  }

  public async delete() {
    this.memoryQueue.push(new DeleteNoteOperation({ store: this.store, payload: { id: this.id } }));
  }

  public async deleteEmptyNote() {
    const [hasSource, hasRecording] = await Promise.all([
      this.store.noteSources.hasAnySourceForNote({ noteId: this.id }),
      this.store.audioCapture.hasRecordingForNote({ noteId: this.id }),
    ]);
    const decision = notesModule.shouldAutoCleanUp({
      id: this.id,
      editor: { isReadOnly: false, isNoteEmpty: true },
      note: {
        isDeleted: this.isDeleted,
        isTrashed: this.isTrashed,
        isOwnedByMe: this.isOwnedByMe,
        isContentLoaded: this.isNoteContentDocumentLoaded,
        modelVersion: this.modelVersion,
        hasQueuedUpdates: this.queuedDocumentUpdates.length > 0,
        hasOpenTab: this.store.navigation.tabs.activeNoteIds.has(this.id),
      },
      runtime: {
        isAudioRecordingActive: this.isAudioRecordingActive,
        hasSuggestion: !!this.suggestionData,
        isAutoDeletable: this.store.notes.autoDeletableNoteIds.has(this.id),
        hasSource,
        hasRecording,
      },
    });

    if (decision.result === null) return;

    if (decision.result === AutoCleanupResult.DeletePermanently) {
      logger.info({ message: `[${this.id}] Deleting note` });
      await this.delete();
      return;
    }

    logger.info({ message: `[${this.id}] Trashing note` });
    this.moveToTrash();
  }

  public async moveToTrash(args?: { triggerSuccessToast?: boolean }) {
    this.memoryQueue.push(
      new TrashNoteOperation({
        ...args,
        store: this.store,
        payload: { id: this.id },
      })
    );
  }

  public async restoreFromTrash(args?: { triggerSuccessToast?: boolean }) {
    this.memoryQueue.push(
      new RestoreNoteOperation({
        ...args,
        store: this.store,
        payload: { id: this.id },
      })
    );
  }

  public async grantAccessViaSpaceAccount({
    roleKind,
    targetSpaceAccountId,
  }: {
    roleKind: GrantableSyncScopeRoleKind;
    targetSpaceAccountId: string;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new GrantNoteAclViaSpaceAccountOperation({
      store: this.store,
      payload: {
        id: this.id,
        space_account_id: targetSpaceAccountId,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async grantAccessViaEmailAddress({
    targetEmailAddress,
    roleKind,
  }: {
    targetEmailAddress: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new GrantNoteAclViaEmailAddressOperation({
      store: this.store,
      payload: {
        id: this.id,
        role_kind: roleKind,
        email_address: targetEmailAddress,
      },
    }).execute();
  }

  public async updateAccessViaSpaceAccount({
    targetSpaceAccountId,
    roleKind,
  }: {
    targetSpaceAccountId: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new UpdateNoteAclViaSpaceAccountOperation({
      store: this.store,
      payload: {
        id: this.id,
        space_account_id: targetSpaceAccountId,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async updateAccessViaEmailAddress({
    targetEmailAddress,
    roleKind,
  }: {
    targetEmailAddress: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new UpdateNoteAclViaEmailAddressOperation({
      store: this.store,
      payload: {
        id: this.id,
        email_address: targetEmailAddress,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async updateAccessViaCollection({
    collectionId,
    roleKind,
  }: {
    collectionId: string;
    roleKind: GrantableSyncScopeRoleKind;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new UpdateNoteAclViaCollectionOperation({
      store: this.store,
      payload: {
        id: this.id,
        collection_id: collectionId,
        role_kind: roleKind,
      },
    }).execute();
  }

  public async revokeAccessViaSpaceAccount({
    targetSpaceAccountId,
  }: {
    targetSpaceAccountId: string;
  }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new RevokeNoteAclViaSpaceAccountOperation({
      store: this.store,
      payload: {
        id: this.id,
        space_account_id: targetSpaceAccountId,
      },
    }).execute();
  }

  public async revokeAccessViaEmailAddress({ targetEmailAddress }: { targetEmailAddress: string }) {
    // TODO: do we want to make it go through the queue or keep it almost automatic?
    await new RevokeNoteAclViaEmailAddressOperation({
      store: this.store,
      payload: {
        id: this.id,
        email_address: targetEmailAddress,
      },
    }).execute();
  }

  public async toggleFavorite() {
    const operation = this.isFavorited
      ? new RemoveNoteFromFavoritesOperation({
          store: this.store,
          payload: { note_id: this.id },
        })
      : new AddNoteToFavoritesOperation({
          store: this.store,
          payload: { note_id: this.id },
        });
    this.memoryQueue.push(operation);
  }

  public addToRecents = async () => {
    this.memoryQueue.push(
      new MarkNoteViewedOperation({
        store: this.store,
        payload: { note_id: this.id },
      })
    );
  };

  public uploadImageAssociatedWithNote({ info }: { info: MemCommonEditorImageInfo }) {
    this.memoryQueue.push(
      new UploadImageExternalOperation({
        store: this.store,
        payload: {
          info,
        },
      })
    );
  }

  public uploadFileAssociatedWithNote({ info }: { info: MemCommonEditorFileInfo }) {
    this.memoryQueue.push(
      new UploadFileExternalOperation({
        store: this.store,
        payload: {
          info,
        },
      })
    );
  }

  public generateCommonEditorContext(): MemCommonEditorContext {
    const uploadImageOperations = (this.store.sync.actionQueue.operationsByOperationKind.get(
      ExternalOperationKind.UPLOAD_IMAGE
    ) ?? []) as UploadImageExternalOperation[];

    const imageInfoMapping: MemCommonEditorContext["images"] = uploadImageOperations.reduce(
      (acc, operation) => {
        const info = operation.payload.info;

        return {
          ...acc,
          [info.imageId]: { info },
        };
      },
      {} as MemCommonEditorContext["images"]
    );

    const uploadFileOperations = (this.store.sync.actionQueue.operationsByOperationKind.get(
      ExternalOperationKind.UPLOAD_FILE
    ) ?? []) as UploadFileExternalOperation[];

    const fileInfoMapping: MemCommonEditorContext["files"] = uploadFileOperations.reduce(
      (acc, operation) => {
        const info = operation.payload.info;

        return {
          ...acc,
          [info.fileId]: { info },
        };
      },
      {} as MemCommonEditorContext["files"]
    );

    return {
      images: imageInfoMapping,
      files: fileInfoMapping,
    };
  }

  private updateLabels(primaryLabel: string, secondaryLabel: string) {
    this.primaryLabel = primaryLabel;
    this.secondaryLabel = secondaryLabel;
  }

  private notifyHeadsUpOfAutoGenerationReadiness({
    primaryLabel,
    isEmpty,
  }: {
    primaryLabel: string;
    isEmpty?: boolean;
  }) {
    if (!primaryLabel.trim()) return;
    if (isEmpty) return;

    this.store.headsUp.markNoteHasPrimaryLabelForAutoGeneration(this.id);
  }

  // SUGGESTIONS
  get isSuggestionActive() {
    return !!this.suggestionData;
  }

  get suggestionData(): SpaceAccountNoteModelData["suggestion_data"] | undefined {
    return this.spaceAccountNote?.modelData.suggestion_data || undefined;
  }

  get isAudioRecordingActive() {
    return this.store.audioCapture.activeNoteId === this.id;
  }

  get hasFinishedAudioRecording() {
    if (!this.isAudioRecordingActive) return false;
    return this.store.audioCapture.hasFinishedAudioRecording;
  }

  setIsProcessingSuggestion(isProcessingSuggestion: boolean) {
    this.isProcessingSuggestion = isProcessingSuggestion;
  }

  async acceptSuggestion() {
    const suggestionData = this.suggestionData;
    if (!suggestionData) return;

    await new AcceptNoteSuggestionOperation({
      store: this.store,
      payload: {
        note_id: this.id,
        encoded_content_diff: suggestionData.suggested_encoded_content_diff,
      },
    }).execute();

    if (
      suggestionData.kind === "VOICE_SESSION_V1" ||
      suggestionData.kind === "VOICE_SESSION_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, transcriptText, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.VoiceSessionSuggestedEditsAccept, {
        session_id: suggestionData.metadata.session_id,
        captured_audio_id: suggestionData.metadata.captured_audio_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        transcript: transcriptText,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_primary_label: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    } else if (
      suggestionData.kind === "NOTE_CLEANUP_V1" ||
      suggestionData.kind === "NOTE_CLEANUP_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.NoteCleanupAccept, {
        session_id: suggestionData.metadata?.session_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    }
  }

  async rejectSuggestion() {
    const suggestionData = this.suggestionData;
    if (!suggestionData) return;

    await new RejectNoteSuggestionOperation({
      store: this.store,
      payload: { note_id: this.id },
    }).execute();

    if (
      suggestionData.kind === "VOICE_SESSION_V1" ||
      suggestionData.kind === "VOICE_SESSION_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, transcriptText, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.VoiceSessionSuggestedEditsReject, {
        session_id: suggestionData.metadata.session_id,
        captured_audio_id: suggestionData.metadata.captured_audio_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        transcript: transcriptText,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    } else if (
      suggestionData.kind === "NOTE_CLEANUP_V1" ||
      suggestionData.kind === "NOTE_CLEANUP_REFINE_V1"
    ) {
      const { previousMarkdownContents, suggestedMarkdownContents, userPrompt } =
        truncateSuggestionDataForTracking(suggestionData);

      trackEvent(TrackedEvent.NoteCleanupReject, {
        session_id: suggestionData.metadata?.session_id,
        note_id: this.id,
        previous_title: suggestionData.prev_primary_label,
        previous_markdown_contents: previousMarkdownContents,
        suggested_markdown_contents: suggestedMarkdownContents,
        suggested_title: suggestionData.suggested_primary_label,
        user_prompt: userPrompt,
      });
    }
  }

  async refineNoteSuggestion(userPrompt: string) {
    if (!this.suggestionData) return;

    const noteId = this.id;
    const sessionId = this.suggestionData.metadata.session_id;

    if (
      this.suggestionData.kind === "VOICE_SESSION_V1" ||
      this.suggestionData.kind === "VOICE_SESSION_REFINE_V1"
    ) {
      const capturedAudioId = this.suggestionData.metadata.captured_audio_id;
      const generatedNoteContent = this.suggestionData.suggested_encoded_content;
      await refineNoteWithVoice({
        noteId,
        userPrompt,
        sessionId,
        capturedAudioId,
        generatedNoteContent,
        suggestionData: this.suggestionData,
        store: this.store,
      });
    } else if (
      this.suggestionData.kind === "NOTE_CLEANUP_V1" ||
      this.suggestionData.kind === "NOTE_CLEANUP_REFINE_V1"
    ) {
      await refineNote({
        noteId,
        userPrompt,
        sessionId,
        store: this.store,
      });
    }
  }

  async cleanup() {
    return await cleanupNote({
      noteId: this.id,
      store: this.store,
    });
  }
}
const METADATA_ID = "METADATA_CHANGED_ID";
